import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import propertyService from './propertyService'

import toast from 'react-hot-toast'
import { authHeader } from '../../utils/headers'
import config from '../../utils/config'

const url = config.testUrl

const initialState = {
	error: false,
	loading: false,
	success: false,
	message: '',
	propertyTypes: [],
	propertyDetails: {},
	properties: [],
	property: {},
	propertyLands: [],
	propertyHouses: [],
}

// get all properties by type
export const get_property_types = createAsyncThunk(
	'propertyTypes/get',
	async (obj, thunkAPI) => {
		try {
			return await propertyService.getPropertyTypes(obj)
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// get all properties (lands)
export const get_property_lands = createAsyncThunk(
	'propertyLands/get',
	async (obj, thunkAPI) => {
		try {
			return await propertyService.getPropertyLands()
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// get all properties (houses)
export const get_property_houses = createAsyncThunk(
	'propertyHouses/get',
	async (obj, thunkAPI) => {
		try {
			return await propertyService.getPropertyHouses()
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// get property details not by admin
export const get_property_details = createAsyncThunk(
	'property_details/get',
	async (id, thunkAPI) => {
		try {
			return await propertyService.getPropertyDetails(id)
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// create new property by admin
export const create_property = createAsyncThunk(
	'create_property/post',
	async (obj, thunkAPI) => {
		try {
			const {
				authLogin: { admin },
			} = thunkAPI.getState()

			const response = await axios.post(`${url}/admin/property`, obj, {
				headers: authHeader(admin.token),
			})

			if (response.data) {
				toast.success('Property has been created!', {
					position: 'top-right',
				})
			}

			return response.data.data
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// create new property
export const get_all_properties = createAsyncThunk(
	'get_all_properties/get',
	async (obj, thunkAPI) => {
		try {
			const {
				authLogin: { admin },
			} = thunkAPI.getState()

			const response = await axios.get(`${url}/admin/property`, {
				headers: authHeader(admin.token),
			})

			return response.data.data
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// update a property
export const update_property = createAsyncThunk(
	'update_property/update',
	async (obj, thunkAPI) => {
		try {
			const {
				authLogin: { admin },
			} = thunkAPI.getState()

			const response = await axios.patch(
				`${url}/admin/property/${obj.id}`,
				obj.data,
				{
					headers: authHeader(admin.token),
				}
			)

			if (response.data) {
				toast.success('Property has been updated!', {
					position: 'top-right',
				})
			}

			return response.data.data
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// delete a property
export const delete_property = createAsyncThunk(
	'delete_property/delete',
	async (id, thunkAPI) => {
		try {
			const {
				authLogin: { admin },
			} = thunkAPI.getState()

			const response = await axios.delete(`${url}/admin/property/${id}`, {
				headers: authHeader(admin.token),
			})

			if (response.data) {
				toast.success('Property has been deleted!', {
					position: 'top-right',
				})
			}

			return response.data.data
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

// Get property details by admin
export const admin_single_property = createAsyncThunk(
	'admin_single_property/get',
	async (id, thunkAPI) => {
		try {
			const {
				authLogin: { admin },
			} = thunkAPI.getState()

			const response = await axios.delete(`${url}/admin/property/${id}`, {
				headers: authHeader(admin.token),
			})

			return response.data.data
		} catch (error) {
			const message =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				error.toString()
			return thunkAPI.rejectWithValue(message)
		}
	}
)

const propertySlice = createSlice({
	name: 'PropertyGetTypes',
	initialState,
	reducers: {
		reset: (state) => {
			state.loading = false
			state.success = false
			state.error = false
			state.message = ''
			state.propertyTypes = null
			state.propertyDetails = null
			state.property = null
			state.properties = null
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(get_property_types.pending, (state) => {
				state.loading = true
			})
			.addCase(get_property_types.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
				state.propertyTypes = action.payload
			})
			.addCase(get_property_types.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.propertyTypes = null
			})
			.addCase(get_property_details.pending, (state) => {
				state.loading = true
			})
			.addCase(get_property_details.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
				state.propertyDetails = action.payload
			})
			.addCase(get_property_details.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.propertyDetails = null
			})
			.addCase(create_property.pending, (state) => {
				state.loading = true
			})
			.addCase(create_property.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
				// state.property = action.payload
			})
			.addCase(create_property.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				// state.property = null
			})
			.addCase(get_all_properties.pending, (state) => {
				state.loading = true
			})
			.addCase(get_all_properties.fulfilled, (state, action) => {
				state.loading = false
				// state.success = true
				state.properties = action.payload
			})
			.addCase(get_all_properties.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.properties = null
			})
			.addCase(update_property.pending, (state) => {
				state.loading = true
			})
			.addCase(update_property.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
				state.property = action.payload
			})
			.addCase(update_property.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.property = null
			})
			.addCase(delete_property.pending, (state) => {
				state.loading = true
			})
			.addCase(delete_property.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
			})
			.addCase(delete_property.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
			})
			.addCase(admin_single_property.pending, (state) => {
				state.loading = true
			})
			.addCase(admin_single_property.fulfilled, (state, action) => {
				state.loading = false
				state.success = true
				state.propertyDetails = action.payload
			})
			.addCase(admin_single_property.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.propertyDetails = null
			})
			.addCase(get_property_lands.pending, (state) => {
				state.loading = true
			})
			.addCase(get_property_lands.fulfilled, (state, action) => {
				state.loading = false
				// state.success = true
				state.propertyLands = action.payload
			})
			.addCase(get_property_lands.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.propertyLands = null
			})
			.addCase(get_property_houses.pending, (state) => {
				state.loading = true
			})
			.addCase(get_property_houses.fulfilled, (state, action) => {
				state.loading = false
				// state.success = true
				state.propertyHouses = action.payload
			})
			.addCase(get_property_houses.rejected, (state, action) => {
				state.loading = false
				state.error = true
				state.message = action.payload
				state.propertyHouses = null
			})
	},
})

export const { reset } = propertySlice.actions
export default propertySlice.reducer
